export type AdTypeKey = 'house' | 'lean' | 'sponsor';
export type AdPlacementKey = 'viewerSplash' | 'hostBreak';

export interface AdTypeConfig {
  videoId?: string;
  imageUrl?: string;
  imageAlt?: string;
  title?: string;
  description?: string;
  ctaText?: string;
  ctaHref?: string;
  color?: string;
  views?: number;
  clicks?: number;
  conversions?: number;
}

export interface PlacementConfig {
  enabledTypes: AdTypeKey[];
  rotationSeconds: number;
}

export type AdConfigDoc = Record<AdTypeKey, AdTypeConfig>;
export type PlacementsDoc = Record<AdPlacementKey, PlacementConfig>;

export const adTypeKeys: AdTypeKey[] = ['house', 'lean', 'sponsor'];

export const adTypeLabels: Record<AdTypeKey, string> = {
  house: 'House Promo',
  lean: 'Core Hosting',
  sponsor: 'Sponsor Spot',
};

const legacyAdTypeMap: Record<string, AdTypeKey> = {
  upgrade: 'house',
  features: 'lean',
  testimonial: 'sponsor',
  house: 'house',
  lean: 'lean',
  sponsor: 'sponsor',
};

export const defaultAdTypeConfig = (): AdTypeConfig => ({
  videoId: '',
  imageUrl: '',
  imageAlt: '',
  title: '',
  description: '',
  ctaText: '',
  ctaHref: '/feedback?from=/party-ad',
  color: 'from-purple-600 to-blue-600',
  views: 0,
  clicks: 0,
  conversions: 0,
});

export function toAdTypeKey(value?: string): AdTypeKey {
  if (!value) return 'house';
  return legacyAdTypeMap[value] ?? 'house';
}

export function normalizeAdConfigDoc(input: unknown): AdConfigDoc {
  const output: AdConfigDoc = {
    house: defaultAdTypeConfig(),
    lean: defaultAdTypeConfig(),
    sponsor: defaultAdTypeConfig(),
  };

  if (!input || typeof input !== 'object') {
    return output;
  }

  for (const [rawKey, rawValue] of Object.entries(input as Record<string, AdTypeConfig>)) {
    const key = toAdTypeKey(rawKey);
    output[key] = {
      ...output[key],
      ...(rawValue || {}),
    };
  }

  return output;
}

export function normalizePlacementsDoc(input: unknown): PlacementsDoc {
  const defaults: PlacementsDoc = {
    viewerSplash: { enabledTypes: [...adTypeKeys], rotationSeconds: 15 },
    hostBreak: { enabledTypes: [...adTypeKeys], rotationSeconds: 20 },
  };

  if (!input || typeof input !== 'object') {
    return defaults;
  }

  const raw = input as Partial<Record<AdPlacementKey, { enabledTypes?: string[]; rotationSeconds?: number }>>;

  return {
    viewerSplash: {
      enabledTypes: (raw.viewerSplash?.enabledTypes || defaults.viewerSplash.enabledTypes).map(toAdTypeKey),
      rotationSeconds: raw.viewerSplash?.rotationSeconds || defaults.viewerSplash.rotationSeconds,
    },
    hostBreak: {
      enabledTypes: (raw.hostBreak?.enabledTypes || defaults.hostBreak.enabledTypes).map(toAdTypeKey),
      rotationSeconds: raw.hostBreak?.rotationSeconds || defaults.hostBreak.rotationSeconds,
    },
  };
}